import type { GetOrdersListItem } from "@birdwatch/client";
import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { useMemo, useState } from "react";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { OrderStatusIcon } from "@/components/ui/status/order-status-icon";
import { Typography } from "@/components/ui/typography";
import { useOrdersByPlaceId } from "@/features/office/places/detail/main/orders/_api/use-orders";
import type { OrderStatus } from "@/types/openapi-enum";

type OrderPlaceIdSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  placeId: string;
  disabled?: boolean;
  "data-test"?: string;
};

export default function OrderPlaceIdSelect({
  className,
  value,
  onChange,
  defaultOpen = false,
  onOpenChange,
  modal = true,
  placeId,
  disabled,
  ...props
}: OrderPlaceIdSelectProps) {
  const [search, setSearch] = useState("");
  const { data: orderOptions = [] } = useOrdersByPlaceId(placeId);

  const options = useMemo<ComboboxOption<GetOrdersListItem>[]>(() => {
    if (!orderOptions) return [];
    return orderOptions
      ?.filter(
        (order) =>
          order.subject?.toLowerCase().includes(search.toLowerCase()) ||
          String(order.orderNumber ?? "")
            .toLowerCase()
            .includes(search.toLowerCase()),
      )
      ?.map((order) => ({
        value: order.id,
        label: order.subject ?? "",
        keywords: [order.subject ?? "", String(order.orderNumber ?? "")],
        data: order,
        detail: (
          <div className="flex grow items-center gap-1">
            <OrderStatusIcon
              status={order.status?.statusKey as OrderStatus}
              size={20}
            />
            <Typography className="text-sm text-text-dark-high ml-1 shrink-0">
              {order.orderNumber}
            </Typography>
            <Typography className="text-sm line-clamp-1">
              {order.subject}
            </Typography>
          </div>
        ),
      }));
  }, [orderOptions, search]);

  return (
    <Combobox
      {...props}
      value={value}
      onChange={onChange}
      options={options}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      onSearchChange={setSearch}
      search={search}
      modal={modal}
      disabled={disabled}
      className={className}
      placeholder="Search for order..."
      searchPlaceholder="Search for order..."
      emptyText="No order found."
      renderTrigger={(selected) =>
        selected ? (
          <div className="flex grow items-center gap-2">
            <OrderStatusIcon
              status={selected.data?.status?.statusKey as OrderStatus}
              size={20}
            />
            <Typography className="flex items-center gap-2 text-text-dark">
              {selected.data?.subject}
            </Typography>
          </div>
        ) : (
          <span className="text-text-dark-min">Search for order...</span>
        )
      }
    />
  );
}
